
'use client';

import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';

export default function MagnifyingGlassAnimation() {
  const [animationPhase, setAnimationPhase] = useState(0);

  useEffect(() => {
    const phases = [
      { x: 10, y: 10 },   // Top-left
      { x: 50, y: 10 },   // Top-center
      { x: 90, y: 10 },   // Top-right
      { x: 90, y: 30 },   // Mid-right
      { x: 90, y: 50 },   // Center-right
      { x: 90, y: 70 },   // Bottom-right
      { x: 50, y: 70 },   // Bottom-center
      { x: 10, y: 70 },   // Bottom-left
      { x: 10, y: 50 },   // Mid-left
      { x: 10, y: 30 },   // Center-left
      { x: 30, y: 30 },   // Inner scan
      { x: 70, y: 30 },
      { x: 70, y: 50 },
      { x: 30, y: 50 },
    ];

    const interval = setInterval(() => {
      setAnimationPhase((prev) => (prev + 1) % phases.length);
    }, 250); // Change position every 250ms

    return () => clearInterval(interval);
  }, []);

  const phases = [
    { x: 10, y: 10 },   // Top-left
    { x: 50, y: 10 },   // Top-center
    { x: 90, y: 10 },   // Top-right
    { x: 90, y: 30 },   // Mid-right
    { x: 90, y: 50 },   // Center-right
    { x: 90, y: 70 },   // Bottom-right
    { x: 50, y: 70 },   // Bottom-center
    { x: 10, y: 70 },   // Bottom-left
    { x: 10, y: 50 },   // Mid-left
    { x: 10, y: 30 },   // Center-left
    { x: 30, y: 30 },   // Inner scan
    { x: 70, y: 30 },
    { x: 70, y: 50 },
    { x: 30, y: 50 },
  ];

  const currentPhase = phases[animationPhase];

  return (
    <div className="absolute inset-0 pointer-events-none">
      {/* Scanning overlay with subtle opacity */}
      <div className="absolute inset-0 bg-blue-500/5 animate-pulse" />
      
      {/* Magnifying glass icon */}
      <div
        className="absolute transition-all duration-300 ease-in-out transform"
        style={{
          left: `${currentPhase.x}%`,
          top: `${currentPhase.y}%`,
          transform: 'translate(-50%, -50%)',
        }}
      >
        {/* Magnifying glass with glow effect */}
        <div className="relative">
          {/* Glow effect */}
          <div className="absolute inset-0 bg-blue-400 rounded-full blur-md opacity-60 animate-pulse scale-150" />
          
          {/* Main magnifying glass */}
          <div className="relative bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg border-2 border-blue-400">
            <Search className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          
          {/* Magnification effect underneath */}
          <div 
            className="absolute top-1/2 left-1/2 w-16 h-16 border-2 border-blue-300 rounded-full opacity-30 animate-ping"
            style={{
              transform: 'translate(-50%, -50%)',
              animationDuration: '1s',
            }}
          />
        </div>
        
        {/* Shadow effect */}
        <div 
          className="absolute top-full left-1/2 w-8 h-2 bg-black/20 rounded-full blur-sm transform -translate-x-1/2 mt-1"
        />
      </div>
      
      {/* Scanning lines effect */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-60 animate-pulse"
          style={{
            top: `${currentPhase.y}%`,
            animationDuration: '0.5s',
          }}
        />
        <div 
          className="absolute h-full w-0.5 bg-gradient-to-b from-transparent via-blue-400 to-transparent opacity-60 animate-pulse"
          style={{
            left: `${currentPhase.x}%`,
            animationDuration: '0.5s',
          }}
        />
      </div>
      
      {/* Corner highlights for scanning effect */}
      <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-blue-400 opacity-60 animate-pulse" />
      <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-blue-400 opacity-60 animate-pulse" />
      <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-blue-400 opacity-60 animate-pulse" />
      <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-blue-400 opacity-60 animate-pulse" />
    </div>
  );
}
